import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';

import Header from '../header/Header';
import Footer from '../footer/Footer';
import BreakModal from '../Modals/BreakModal';
import { decodeToken } from '../../utils/jwtUtils';
import { clearPersister } from '../../utils/common';
import CallingSidebar from '../callingSidebar/CallingSidebar';
import { initializeSocket, socket } from '../../config/Socket';
import whatsappIcon from '../../assets/images/icons/whatsapp.svg';
import { CallingContextProvider } from '../Context/CallingContext';
import ProcessChangeConfirmationModal from '../Modals/ProcessChangeConfirmationModal';

import {
  authLogout,
  authSwitchProcess,
  useToken,
  useUserDetails
} from '../../store/slices/authSlice';
import {
  setCallingModalOpen,
  setTimezone,
  useCallingModal,
  useCurrentSidebarType
} from '../../store/slices/commonSlice';
import {
  setChatBotHistory,
  setIsDialNumber,
  useBreakDetails,
  useCallId,
  useCallingStatus,
  useChatBotHistory
} from '../../store/slices/dialSlice';
import {
  getChatCount,
  markAsRead,
  setActiveChatDetails,
  useActiveChatDetails
} from '../../store/slices/tenantSlices/chatSlice';
import {
  helpMarkAsRead,
  setActiveHelpDetails,
  unreadCountGet,
  useActiveHelpDetails
} from '../../store/slices/tenantSlices/helpDeskSlice';
import {
  followUpStatusUpdate,
  pauseModeUpdate,
  setPauseMode
} from '../../store/slices/callingSlice';
import {
  metaHelpMarkAsRead,
  setActiveMetaHelpDetails,
  unreadMetaCountGet,
  useActiveMetaHelpDetails
} from '../../store/slices/tenantSlices/metaHelpDeskSlice';
import { missedFollowUpGet } from '../../store/slices/tenantSlices/dailyReportSlice';
import { initAudioContext } from '../../utils/audioUtils';

const MainLayout = () => {
  const isEmbedded = window.self !== window.top;

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const token = useToken();
  const callingModalOpen = useCallingModal();
  const callId = useCallId();
  const user = useUserDetails();
  const breakDetails = useBreakDetails();
  const activeChatDetails = useActiveChatDetails();
  const activeHelpDetails = useActiveHelpDetails();
  const activeMetaHelpDetails = useActiveMetaHelpDetails();
  const callStatus = useCallingStatus();
  const chatBotHistory = useChatBotHistory();
  const currentSidebarType = useCurrentSidebarType();

  const [isProcessModalOpen, setIsProcessModalOpen] = useState({
    isOpen: false,
    selectedProcess: null
  });
  const [isProcessLoading, setIsProcessLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('callex-token');
    if (!token) {
      clearPersister(dispatch);
    }
  }, []);

  const handleBeforeUnload = (event) => {
    const message =
      "The page that you're looking for used information that you entered. Returning to that page might cause any action you took to be repeated. Do you want to continue?";
    event.returnValue = message;
    return message;
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const onLogout = async () => {
    try {
      await dispatch(authLogout()).unwrap();
      socket.emit('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
      socket.disconnect();
      socket.off('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
      clearPersister(dispatch);
      window.__SIP_MANUAL_LOGOUT__ = true;
      localStorage.clear();
      localStorage.removeItem('timerDuration');
      localStorage.removeItem('timerStartTime');
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    socket.on('logout_realtime', ({ message }) => {
      console.log('message', message);
      onLogout();
      // Inside an iframe there is no login screen to fall back to, so show a
      // dedicated "session ended" info page instead of a blank/unauthorized view.
      if (isEmbedded) {
        navigate('/session-ended', { replace: true });
      }
    });
    return () => {
      socket.off('logout_realtime');
    };
  }, []);

  const fetchChatCount = async () => {
    try {
      const payload = {
        userId: user?.user_id,
        userRole: 'agent'
      };
      await dispatch(getChatCount(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchHelpChatCount = async () => {
    try {
      const payload = {
        to: user?.to_whatsapp_number
      };
      await dispatch(unreadCountGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onMarkAsReadHelp = async (from, to) => {
    try {
      const payload = {
        from: from,
        to: to
      };
      await dispatch(helpMarkAsRead(payload)).unwrap();
      fetchHelpChatCount();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchMetaHelpChatCount = async () => {
    try {
      const payload = {
        to: user?.to_whatsapp_number
      };
      await dispatch(unreadMetaCountGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onMarkAsReadMetaHelp = async (from, to) => {
    try {
      const payload = {
        from: from,
        to: to
      };
      await dispatch(metaHelpMarkAsRead(payload)).unwrap();
      fetchMetaHelpChatCount();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onMarkAsRead = async (countObj) => {
    try {
      const payload = {
        toUserId: user?.user_id,
        toRole: 'agent',
        fromUserId: countObj?.fromUserId,
        fromRole: countObj?.fromRole
      };
      await dispatch(markAsRead(payload)).unwrap();
      fetchChatCount();
    } catch (error) {
      console.log('error', error);
    }
  };

  const getMissedFollowUp = async () => {
    try {
      const response = await dispatch(missedFollowUpGet()).unwrap();
      if (response && response?.data?.length > 0) {
        response?.data?.map((x) =>
          toast(
            ({ closeToast }) => (
              <div
                className="d-flex align-items-center gap-2 cursor-pointer"
                onClick={async () => {
                  try {
                    await dispatch(
                      followUpStatusUpdate({
                        follow_up_status: 1,
                        id: x?.id
                      })
                    ).unwrap();
                    dispatch(setIsDialNumber(x?.phone_number));
                    closeToast();
                  } catch (err) {
                    console.error('Failed to update follow-up status:', err);
                  }
                }}>
                <Icon.Clock size={30} className="text-white" />
                <div className="d-flex flex-column justify-content-start cursor-pointer w-100">
                  <div className="d-flex gap-3 align-items-center">
                    <strong
                      style={{
                        textTransform: 'capitalize',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                      Follow Up
                    </strong>
                    <Icon.PhoneForwarded size={16} className="text-green" />
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '250px'
                    }}>
                    {x?.phone_number}
                  </div>
                </div>
              </div>
            ),
            {
              className: 'custom-follow-up-toast',
              position: 'top-right',
              autoClose: false,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true
            }
          )
        );
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onPauseModeChange = async (user) => {
    try {
      const payload = {
        user_id: user?.id,
        pause_mode:
          user?.process_details?.type === 'OutboundAndPredictive' ||
            user?.process_details?.type === 'BlendedAndPredictive'
            ? 1
            : 0
      };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSwitchProcessConfirm = async () => {
    setIsProcessLoading(true);
    try {
      const payload = {
        new_process_id: isProcessModalOpen.selectedProcess.id
      };
      const response = await dispatch(authSwitchProcess(payload)).unwrap();
      if (response && response.success) {
        const user = decodeToken(response?.token);
        dispatch(setTimezone(user?.time_zone_details?.zone_name));
        initializeSocket();
        dispatch(setCallingModalOpen(user?.manualcall));
        getMissedFollowUp();
        if (process.env.REACT_APP_AGENT_PAUSE_MODE === 'true') {
          onPauseModeChange(user);
        }
        navigate('/dashboard');
        setIsProcessLoading(false);
        setIsProcessModalOpen({ isOpen: false, selectedProcess: null });
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (
      (user?.process_details?.chat_with_supervisor || user?.process_details?.chat_with_user) &&
      !isEmbedded
    ) {
      fetchChatCount();
    }
    if (user?.process_details?.process_details && !isEmbedded) {
      fetchHelpChatCount();
    }
  }, [user, isEmbedded]);

  useEffect(() => {
    const handleChatMessage = (message) => {
      console.log('message------->', message);

      const canChat =
        user?.process_details?.chat_with_supervisor || user?.process_details?.chat_with_user;

      if (!canChat) return;

      if (message?.fromUserId === activeChatDetails?.id && location.pathname === '/chat') {
        onMarkAsRead({ ...message });
      } else {
        toast(
          <div>
            <strong
              style={{ textTransform: 'capitalize', fontWeight: 'bold', marginBottom: '4px' }}>
              {message?.fromRole}
            </strong>
            <div
              style={{
                fontSize: '13px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '250px'
              }}>
              {message?.filePath ? 'Attachment' : message?.message}
            </div>
          </div>,
          {
            position: 'top-right',
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          }
        );
        fetchChatCount();
      }
    };

    socket.on('chatMessage', handleChatMessage);

    return () => {
      socket.off('chatMessage', handleChatMessage);
    };
  }, [activeChatDetails, location.pathname, user]);

  useEffect(() => {
    const handleChatMessage = (message) => {
      console.log('message------->', message);
      const canChat = user?.userWhatsappPermission;

      if (!canChat) return;

      if (message?._id === activeMetaHelpDetails?.id && location.pathname === '/meta-help-desk') {
        onMarkAsReadMetaHelp(message?.from_whatsapp_number, message?.to_whatsapp_number);
      } else {
        toast(
          ({ closeToast }) => (
            <div
              className="d-flex align-items-center gap-2"
              onClick={() => {
                dispatch(
                  setActiveHelpDetails({
                    ...message,
                    id: message?._id,
                    name: message?.from_name,
                    activeTab: 'mine'
                  })
                );
                onMarkAsReadMetaHelp(message?.from_whatsapp_number, message?.to_whatsapp_number);
                navigate('/meta-help-desk');
                closeToast();
              }}
              style={{ cursor: 'pointer' }}>
              <img src={whatsappIcon} width={30} height={30} />
              <div className="d-flex flex-column justify-content-start">
                <strong
                  style={{ textTransform: 'capitalize', fontWeight: 'bold', marginBottom: '4px' }}>
                  {message?.from_name}
                </strong>
                <div
                  style={{
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '250px'
                  }}>
                  {message?.body}
                </div>
              </div>
            </div>
          ),
          {
            className: 'custom-whatsapp-toast',
            position: 'top-right',
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true
          }
        );
        fetchMetaHelpChatCount();
      }
    };

    socket.on('received-whatsapp-meta-message', handleChatMessage);

    return () => {
      socket.off('received-whatsapp-meta-message', handleChatMessage);
    };
  }, [activeMetaHelpDetails, location.pathname, user]);

  useEffect(() => {
    const handleChatMessage = (message) => {
      const canChat = user?.userWhatsappPermission;

      if (!canChat) return;
      console.log('message------->', message);
      if (message?._id === activeMetaHelpDetails?.id && location.pathname === '/meta-help-desk') {
        return false;
      }
      toast(
        ({ closeToast }) => (
          <div
            className="d-flex align-items-center gap-2"
            onClick={() => {
              dispatch(
                setActiveHelpDetails({
                  ...message,
                  id: message?._id,
                  name: message?.from_name,
                  activeTab: 'unassigned'
                })
              );
              navigate('/meta-help-desk');
              closeToast();
            }}
            style={{ cursor: 'pointer' }}>
            <img src={whatsappIcon} width={30} height={30} />
            <div className="d-flex flex-column justify-content-start">
              <strong
                style={{ textTransform: 'capitalize', fontWeight: 'bold', marginBottom: '4px' }}>
                {message?.from_name}
              </strong>
              <div
                style={{
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '250px'
                }}>
                {message?.message}
              </div>
            </div>
          </div>
        ),
        {
          className: 'custom-whatsapp-toast',
          position: 'top-right',
          autoClose: false,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true
        }
      );
    };

    socket.on('unassign-whatsapp-meta-notify', handleChatMessage);

    return () => {
      socket.off('unassign-whatsapp-meta-notify', handleChatMessage);
    };
  }, [activeMetaHelpDetails, location.pathname, user]);

  useEffect(() => {
    const handleChatMessage = (message) => {
      console.log('message------->', message);
      const canChat = user?.userWhatsappPermission;

      if (!canChat) return;

      if (message?._id === activeHelpDetails?.id && location.pathname === '/help-desk') {
        onMarkAsReadHelp(message?.from_whatsapp_number, message?.to_whatsapp_number);
      } else {
        toast(
          ({ closeToast }) => (
            <div
              className="d-flex align-items-center gap-2"
              onClick={() => {
                dispatch(
                  setActiveHelpDetails({
                    ...message,
                    id: message?._id,
                    name: message?.from_name,
                    activeTab: 'mine'
                  })
                );
                onMarkAsReadHelp(message?.from_whatsapp_number, message?.to_whatsapp_number);
                navigate('/help-desk');
                closeToast();
              }}
              style={{ cursor: 'pointer' }}>
              <img src={whatsappIcon} width={30} height={30} />
              <div className="d-flex flex-column justify-content-start">
                <strong
                  style={{ textTransform: 'capitalize', fontWeight: 'bold', marginBottom: '4px' }}>
                  {message?.from_name}
                </strong>
                <div
                  style={{
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '250px'
                  }}>
                  {message?.body}
                </div>
              </div>
            </div>
          ),
          {
            className: 'custom-whatsapp-toast',
            position: 'top-right',
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true
          }
        );
        fetchHelpChatCount();
      }
    };

    socket.on('new-message', handleChatMessage);

    return () => {
      socket.off('new-message', handleChatMessage);
    };
  }, [activeHelpDetails, location.pathname, user]);

  useEffect(() => {
    const handleChatMessage = (message) => {
      const canChat = user?.userWhatsappPermission;

      if (!canChat) return;
      console.log('message------->', message);
      if (message?._id === activeHelpDetails?.id && location.pathname === '/help-desk') {
        return false;
      }
      toast(
        ({ closeToast }) => (
          <div
            className="d-flex align-items-center gap-2"
            onClick={() => {
              dispatch(
                setActiveHelpDetails({
                  ...message,
                  id: message?._id,
                  name: message?.from_name,
                  activeTab: 'unassigned'
                })
              );
              navigate('/help-desk');
              closeToast();
            }}
            style={{ cursor: 'pointer' }}>
            <img src={whatsappIcon} width={30} height={30} />
            <div className="d-flex flex-column justify-content-start">
              <strong
                style={{ textTransform: 'capitalize', fontWeight: 'bold', marginBottom: '4px' }}>
                {message?.from_name}
              </strong>
              <div
                style={{
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '250px'
                }}>
                {message?.message}
              </div>
            </div>
          </div>
        ),
        {
          className: 'custom-whatsapp-toast',
          position: 'top-right',
          autoClose: false,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true
        }
      );
    };

    socket.on('unassign-whatsapp-notify', handleChatMessage);

    return () => {
      socket.off('unassign-whatsapp-notify', handleChatMessage);
    };
  }, [activeHelpDetails, location.pathname, user]);

  useEffect(() => {
    const handleFetchFollowUp = async (message) => {
      console.log('message-followUp------>', message);

      if (message?.auto_flag === 'disable' || (message?.auto_flag === 'enable' && callStatus)) {
        toast(
          ({ closeToast }) => (
            <div
              className="d-flex align-items-center gap-2 cursor-pointer"
              onClick={async () => {
                try {
                  await dispatch(
                    followUpStatusUpdate({
                      follow_up_status: 1,
                      id: message?.id
                    })
                  ).unwrap();
                  dispatch(setIsDialNumber(message?.phone_number));
                  closeToast();
                } catch (err) {
                  console.error('Failed to update follow-up status:', err);
                }
              }}>
              <Icon.Clock size={30} className="text-white" />
              <div className="d-flex flex-column justify-content-start cursor-pointer w-100">
                <div className="d-flex gap-3 align-items-center">
                  <strong
                    style={{
                      textTransform: 'capitalize',
                      fontWeight: 'bold',
                      marginBottom: '4px'
                    }}>
                    Follow Up
                  </strong>
                  <Icon.PhoneForwarded size={16} className="text-green" />
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '250px'
                  }}>
                  {message?.phone_number}
                </div>
              </div>
            </div>
          ),
          {
            className: 'custom-follow-up-toast',
            position: 'top-right',
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true
          }
        );
      } else {
        try {
          await dispatch(
            followUpStatusUpdate({
              follow_up_status: 1,
              id: message?.id
            })
          ).unwrap();
          dispatch(setIsDialNumber(message?.phone_number));
        } catch (err) {
          console.error('Failed to update follow-up and set dial number:', err);
        }
      }
    };

    socket.on('followUp-notify', handleFetchFollowUp);

    return () => {
      socket.off('followUp-notify', handleFetchFollowUp);
    };
  }, [dispatch, callStatus]); // only include necessary deps

  useEffect(() => {
    if (location.pathname !== '/chat') {
      dispatch(setActiveChatDetails(null));
    }
    if (location.pathname !== '/help-desk') {
      dispatch(setActiveHelpDetails(null));
    }
    if (location.pathname !== '/meta-help-desk') {
      dispatch(setActiveMetaHelpDetails(null));
    }
    if (location.pathname === '/help-desk' || location.pathname === '/meta-help-desk') {
      dispatch(setCallingModalOpen(false));
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleChatBotMessage = (message) => {
      console.log('bot_answer ------->', message);
      const newMessage = [message, ...(chatBotHistory || [])];
      dispatch(setChatBotHistory(newMessage));
    };
    socket.on('bot_answer', handleChatBotMessage);
    return () => {
      socket.off('bot_answer');
    };
  }, [user, chatBotHistory]);

  useEffect(() => {
    if (currentSidebarType === 'main' || !callStatus) {
      dispatch(setChatBotHistory([]));
    }
  }, [currentSidebarType, callStatus]);

  if (!token && !isEmbedded) {
    return <Navigate to="/login" replace />;
  }

  initAudioContext();

  const canOpenCallingSidebar = user?.manualcall || !!callId;

  return (
    <CallingContextProvider>
      <Header />
      <audio id="mediaElement" controls style={{ display: 'none' }}></audio>

      <div className={`d-flex main-container ${!callingModalOpen ? 'calling-modal-close' : ''}`}>
        <div className="outlet-container">
          <div className="callex-container">
            <Outlet />
          </div>
          <Footer />
        </div>

        {/* CallingSidebar is the communication engine; it stays mounted to handle SIP/Sockets */}
        <div className={`calling-container ${callingModalOpen ? 'slide-in' : ''}`}>
          <CallingSidebar setIsProcessModalOpen={setIsProcessModalOpen} />
        </div>
      </div>

      {!callingModalOpen && (
        <div
          className="call-icon-container"
          onClick={() => canOpenCallingSidebar && dispatch(setCallingModalOpen(true))}
        >
          {/* Small 'Predictive' modal display when sidebar is closed and user has no manual call */}
          {!user?.manualcall && !callId && (
            <CallingSidebar setIsProcessModalOpen={setIsProcessModalOpen} isMini={true} />
          )}
          <Icon.PhoneCall />
        </div>
      )}
      {!!breakDetails && <BreakModal />}
      {isProcessModalOpen && (
        <ProcessChangeConfirmationModal
          isOpen={isProcessModalOpen.isOpen}
          onCloseClick={() => setIsProcessModalOpen({ isOpen: false, selectedProcess: null })}
          onConfirmClick={() => {
            onSwitchProcessConfirm();
          }}
          isLoading={isProcessLoading}
        />
      )}
    </CallingContextProvider>
  );
};

export default MainLayout;
